import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Wallet, Percent } from "@phosphor-icons/react";
import { throwIfNotOk } from "@/lib/api-error";
import { CreditTransactionsTable } from "@/components/admin/companies/CreditTransactionsTable";
import type {
  CompanyCreditTransactionsResponse,
  MyCompanyCredit,
} from "@/types";

export const Route = createFileRoute("/dashboard/supervisor/credit")({
  component: SupervisorCreditPage,
});

const PAGE_SIZE = 25;

function fmtKm(n: number): string {
  return `${new Intl.NumberFormat("bs-BA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n)} KM`;
}

function SupervisorCreditPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);

  const summary = useQuery({
    queryKey: ["supervisor", "credit"],
    queryFn: async () => {
      const res = await fetch("/api/supervisor/credit");
      await throwIfNotOk(res, t("common.errorLoading"));
      return res.json() as Promise<MyCompanyCredit>;
    },
  });

  const ledger = useQuery({
    queryKey: ["supervisor", "credit", "transactions", page],
    queryFn: async () => {
      const res = await fetch(
        `/api/supervisor/credit/transactions?limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`,
      );
      await throwIfNotOk(res, t("common.errorLoading"));
      return res.json() as Promise<CompanyCreditTransactionsResponse>;
    },
  });

  const balance = ledger.data?.balance ?? summary.data?.balance ?? 0;
  const discountPercent = summary.data?.discountPercent ?? 0;

  return (
    <div className="space-y-5">
      <div>
        <h1
          className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {t("supervisor.credit.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("supervisor.credit.subtitle")}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-border/60 bg-card p-5 shadow-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Wallet className="size-4" />
            <span className="text-[11px] font-semibold uppercase tracking-widest">
              {t("supervisor.credit.balance")}
            </span>
          </div>
          <p className="mt-2 text-3xl font-bold tabular-nums text-foreground">
            {summary.isLoading ? "…" : fmtKm(balance)}
          </p>
        </div>

        <div className="rounded-xl border border-border/60 bg-card p-5 shadow-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Percent className="size-4" />
            <span className="text-[11px] font-semibold uppercase tracking-widest">
              {t("supervisor.credit.discount")}
            </span>
          </div>
          <p className="mt-2 text-3xl font-bold tabular-nums text-foreground">
            {summary.isLoading ? "…" : `${discountPercent}%`}
          </p>
        </div>
      </div>

      <CreditTransactionsTable
        invoiceUrl={(txId) => `/api/supervisor/invoice?transactionId=${txId}`}
        items={ledger.data?.items ?? []}
        total={ledger.data?.total ?? 0}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />
    </div>
  );
}
